import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useRejectPair } from '../api/usePairMapper';
import { useFixMatch } from '../api/useEventAgent';

interface Props {
  pairId: string | null;
  pairName?: string;
  onClose: () => void;
  /** Called with the fix-match run id when the operator gave a reason. */
  onFixMatchStarted: (runId: string) => void;
}

/**
 * Unmatch a pair. Reason-gated: an empty reason is a plain unmatch; a filled
 * reason additionally dispatches a fix-match agent run that reviews the
 * matcher and (only for clear cases) opens a draft matching-core PR.
 */
export const RejectPairDialog = ({
  pairId,
  pairName,
  onClose,
  onFixMatchStarted,
}: Props) => {
  const [reason, setReason] = useState('');
  const rejectPair = useRejectPair();
  const fixMatch = useFixMatch();
  const busy = rejectPair.isLoading || fixMatch.isLoading;
  const hasReason = reason.trim().length > 0;

  const handleClose = () => {
    if (busy) return;
    setReason('');
    rejectPair.reset();
    fixMatch.reset();
    onClose();
  };

  const handleUnmatch = async () => {
    if (!pairId) return;
    if (hasReason) {
      // fix-match FIRST — the endpoint snapshots the pair before the reject
      // below deletes it.
      const run = await fixMatch.mutateAsync({ pairId, reason: reason.trim() });
      await rejectPair.mutateAsync(pairId);
      setReason('');
      rejectPair.reset();
      fixMatch.reset();
      onClose();
      onFixMatchStarted(run._id);
    } else {
      await rejectPair.mutateAsync(pairId);
      handleClose();
    }
  };

  return (
    <Dialog open={!!pairId} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Unmatch pair</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {pairName && (
            <Typography variant="body2" color="text.secondary">
              {pairName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            The pair is unlinked and won't re-match. Optionally say <em>why</em>{' '}
            the match was wrong — that dispatches the fix-match agent, which
            reviews the matcher and, only for clear cases, opens a draft PR for
            you to review.
          </Typography>
          <TextField
            label="Why was this match wrong? (optional)"
            placeholder="e.g. paired O/U 9.5 corners to Kalshi 9+ — should be 10+"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            autoFocus
          />
          {(rejectPair.isError || fixMatch.isError) && (
            <Alert severity="error">
              Something failed — the pair may or may not be unmatched. Re-check
              the mapper.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleUnmatch}
          disabled={!pairId || busy}
          startIcon={
            busy ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {hasReason ? 'Unmatch & review matcher' : 'Unmatch'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
